import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, UserPlus, Copy, Check, Inbox, Send, X, Trophy, RotateCcw, Swords } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCoop } from "../contexts/CoopContext";
import { useSocial } from "../contexts/SocialContext";
import { SFX, playMusic } from "../utils/audio";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FriendCard = ({ friend, action, actionLabel, actionStyle = "bg-rose-500", testId, online = false, onView }) => (
  <div data-testid={testId} className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static flex items-center gap-3">
    <button onClick={onView} disabled={!onView} className="relative flex-shrink-0 disabled:cursor-default">
      {friend.picture ? (
        <img src={friend.picture} alt={friend.name} className="w-12 h-12 rounded-full border-2 border-slate-950" />
      ) : (
        <div className="w-12 h-12 rounded-full border-2 border-slate-950 bg-slate-700 flex items-center justify-center font-bold">{friend.name?.[0] || "?"}</div>
      )}
      {onView && (
        <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-slate-800 ${online ? "bg-emerald-400" : "bg-slate-600"}`} />
      )}
    </button>
    <div className="flex-1 min-w-0">
      <button onClick={onView} disabled={!onView} className="font-display font-bold truncate block text-left disabled:cursor-default hover:text-sky-300">{friend.name}</button>
      <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
        <span className="flex items-center gap-1"><Trophy className="w-3 h-3 text-yellow-400" /> Ét. {friend.highest_floor || 0}</span>
        <span className="flex items-center gap-1"><RotateCcw className="w-3 h-3 text-sky-400" /> {friend.total_runs || 0} runs</span>
      </div>
    </div>
    {action && (
      <button onClick={action} className={`${actionStyle} text-white border-4 border-slate-950 rounded-xl px-3 py-1.5 text-sm font-bold cartoon-shadow`}>
        {actionLabel}
      </button>
    )}
  </div>
);

const Friends = () => {
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const { lobby, createLobby, inviteFriend } = useCoop();
  const { isOnline, openProfile } = useSocial();
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleInviteToLobby = async (friend) => {
    SFX.click();
    try {
      let activeLobby = lobby;
      if (!activeLobby || activeLobby.status !== "waiting") {
        activeLobby = await createLobby("");
      }
      await inviteFriend(friend.user_id);
      toast.success(`${friend.name} a été invité dans le lobby ${activeLobby.code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'invitation");
    }
  };

  useEffect(() => { playMusic("menu"); }, []);

  const fetchFriends = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/friends`, { withCredentials: true });
      setFriends(res.data.friends || []);
      setIncoming(res.data.incoming || []);
      setOutgoing(res.data.outgoing || []);
    } catch (e) {
      console.warn("Fetch friends failed", e);
    }
  }, [user]);

  useEffect(() => { fetchFriends(); }, [fetchFriends]);

  const handleAdd = async () => {
    if (!code.trim()) return;
    SFX.click();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/friends/request`, { friend_code: code.trim() }, { withCredentials: true });
      SFX.notify();
      if (res.data.status === "accepted") {
        toast.success("Ami ajouté ! (demande mutuelle)");
      } else {
        toast.success("Demande envoyée !");
      }
      setCode("");
      fetchFriends();
    } catch (e) {
      SFX.miss();
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const acceptReq = async (req) => {
    SFX.click();
    try {
      await axios.post(`${API}/friends/accept/${req.request_id}`, {}, { withCredentials: true });
      SFX.victory();
      toast.success(`${req.name} est maintenant votre ami !`);
      fetchFriends();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const rejectReq = async (req) => {
    SFX.click();
    try {
      await axios.post(`${API}/friends/reject/${req.request_id}`, {}, { withCredentials: true });
      toast.info("Demande rejetée");
      fetchFriends();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const cancelReq = async (req) => {
    SFX.click();
    try {
      await axios.post(`${API}/friends/cancel/${req.request_id}`, {}, { withCredentials: true });
      toast.info("Demande annulée");
      fetchFriends();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const removeFriend = async (friend) => {
    SFX.click();
    try {
      await axios.delete(`${API}/friends/${friend.user_id}`, { withCredentials: true });
      toast.info(`${friend.name} retiré de la liste`);
      fetchFriends();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const copyCode = async () => {
    if (!user?.friend_code) return;
    SFX.click();
    try {
      await navigator.clipboard.writeText(user.friend_code);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Échec de la copie");
    }
  };

  if (authLoading) return <div className="h-screen flex items-center justify-center text-slate-300">Chargement...</div>;

  if (!user) {
    return (
      <div className="h-screen w-screen bg-slate-900 bg-dots flex flex-col items-center justify-center p-6">
        <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-8 cartoon-shadow-lg-static max-w-md text-center">
          <UserPlus className="w-16 h-16 mx-auto text-sky-400 mb-4" />
          <h2 className="font-display text-2xl font-bold mb-2">Connexion requise</h2>
          <p className="text-slate-300 text-sm mb-4">Connecte-toi pour ajouter et gérer tes amis.</p>
          <button data-testid="friends-login-btn" onClick={() => { SFX.click(); loginWithGoogle(); }} className="bg-white text-slate-950 border-4 border-slate-950 rounded-xl px-4 py-2 cartoon-shadow font-bold">Se connecter avec Google</button>
          <button onClick={() => navigate("/")} className="block mt-3 mx-auto text-slate-400 text-sm">Retour au menu</button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots flex flex-col overflow-hidden">
      <Toaster theme="dark" position="top-center" />
      <div className="flex items-center justify-between px-6 py-4 border-b-4 border-slate-950 bg-slate-800">
        <button
          data-testid="friends-back"
          onClick={() => { SFX.click(); navigate("/"); }}
          className="bg-slate-700 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Menu
        </button>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Amis</h1>
        <div className="w-24" />
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto space-y-5">

          {/* Mon code ami */}
          <div className="bg-gradient-to-br from-sky-500 to-indigo-500 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static">
            <p className="text-xs uppercase tracking-wider font-bold text-white/80 mb-2">Ton code ami</p>
            <div className="flex items-center gap-3">
              <span data-testid="my-friend-code" className="font-display text-3xl sm:text-4xl font-bold tracking-widest bg-slate-950 text-yellow-400 border-4 border-slate-950 rounded-2xl px-4 py-2">
                {user.friend_code || "------"}
              </span>
              <button data-testid="copy-friend-code" onClick={copyCode} className="bg-yellow-400 text-slate-950 border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow font-bold">
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-white/80 mt-2">Partage ce code avec tes amis pour qu'ils t'ajoutent.</p>
          </div>

          {/* Ajouter un ami */}
          <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4 text-sky-400" /> Ajouter un ami</h3>
            <div className="flex gap-2">
              <input
                data-testid="friend-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Code ami (6 caractères)"
                maxLength={6}
                className="flex-1 bg-slate-900 border-4 border-slate-950 rounded-xl px-3 py-2 font-display tracking-widest text-lg uppercase placeholder:text-slate-500"
              />
              <button
                data-testid="add-friend-btn"
                onClick={handleAdd}
                disabled={loading || code.length !== 6}
                className="bg-sky-400 text-slate-950 border-4 border-slate-950 rounded-xl px-4 py-2 font-display font-bold cartoon-shadow disabled:opacity-40"
              >
                {loading ? "..." : "Ajouter"}
              </button>
            </div>
          </div>

          {/* Demandes reçues */}
          {incoming.length > 0 && (
            <div>
              <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
                <Inbox className="w-4 h-4" /> Demandes reçues ({incoming.length})
              </h3>
              <div className="space-y-2">
                {incoming.map((req) => (
                  <div key={req.request_id} className="flex items-center gap-2">
                    <div className="flex-1">
                      <FriendCard friend={req} testId={`incoming-${req.user_id}`} />
                    </div>
                    <button data-testid={`accept-${req.user_id}`} onClick={() => acceptReq(req)} className="bg-emerald-500 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow font-bold text-sm">✓</button>
                    <button data-testid={`reject-${req.user_id}`} onClick={() => rejectReq(req)} className="bg-rose-500 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow font-bold text-sm"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Demandes envoyées */}
          {outgoing.length > 0 && (
            <div>
              <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
                <Send className="w-4 h-4" /> En attente ({outgoing.length})
              </h3>
              <div className="space-y-2">
                {outgoing.map((req) => (
                  <FriendCard
                    key={req.request_id}
                    friend={req}
                    action={() => cancelReq(req)}
                    actionLabel="Annuler"
                    actionStyle="bg-slate-600"
                    testId={`outgoing-${req.user_id}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Amis */}
          <div>
            <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-2">Mes amis ({friends.length})</h3>
            {friends.length === 0 ? (
              <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-8 text-center text-slate-500">
                <p className="font-display">Aucun ami pour le moment.</p>
                <p className="text-xs mt-1">Partage ton code ami pour commencer ton aventure ensemble !</p>
              </div>
            ) : (
              <div className="space-y-2">
                {friends.map((f) => (
                  <div key={f.user_id} className="flex items-center gap-2">
                    <div className="flex-1">
                      <FriendCard friend={f} testId={`friend-${f.user_id}`} online={isOnline(f.user_id)} onView={() => openProfile(f.user_id)} />
                    </div>
                    <button
                      data-testid={`invite-coop-${f.user_id}`}
                      onClick={() => handleInviteToLobby(f)}
                      title="Inviter en co-op"
                      className="bg-fuchsia-500 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow font-bold text-sm flex items-center gap-1"
                    >
                      <Swords className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`remove-friend-${f.user_id}`}
                      onClick={() => removeFriend(f)}
                      title="Retirer"
                      className="bg-slate-600 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow font-bold text-sm"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Friends;
